import { getFirmName } from '@deltek/specification-client/core/Widgets/ProductListing/groupedTreeUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from '@material-ui/lab/Autocomplete'
import React, { ChangeEvent, useCallback, useMemo } from 'react'

interface AutoCompleteInputProps {
    name: string
    items: string[]
    label?: string
    loading?: boolean
    selectedValue?: string
    disabled?: boolean
    handleChange: (
        event: ChangeEvent<{}>,
        value: string | null,
        reason: AutocompleteChangeReason
    ) => void
    handleInputChange: (
        event: ChangeEvent<{}>,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => void
    handleOpen?: (event: ChangeEvent<{}>) => void
    formState: Record<string, unknown>
    hasError?: boolean
    listBoxProps?: object
}

const useStyles = makeStyles((theme: Theme) => ({
    autoCompleteInput: {
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            padding: `0 ${theme.spacing(8)}px 0 ${theme.spacing(1)}px`,
        },
        '& .MuiAutocomplete-clearIndicator': {
            marginTop: -theme.spacing(0.5),
        },
        '& label': {
            transform: `translate(${theme.spacing(1.75)}px, ${theme.spacing(
                1.5
            )}px) scale(1)`,
        },
    },

    autocompleteAdornment: {
        maxHeight: 50,
        overflowY: 'auto',
    },
}))

export const AutoCompleteInput: React.FC<AutoCompleteInputProps> = ({
    name,
    items,
    label,
    handleChange,
    selectedValue,
    loading,
    disabled = false,
    handleOpen,
    handleInputChange,
    formState,
    hasError,
    listBoxProps,
}) => {
    const classes = useStyles()
    const premiumPlusProductNameIdentifier = '(+)'
    const getOptions = useMemo(() => {
        let itemsList = items
        if (name === 'productType')
            itemsList = items.map((option) => {
                const primumPlusArr = option.split('%|%')
                return `${
                    primumPlusArr.includes('null')
                        ? ''
                        : premiumPlusProductNameIdentifier
                } ${primumPlusArr.shift()}`
            })

        return itemsList
    }, [name, items])

    const handleAutocompleteChange = useCallback(
        (
            event: ChangeEvent<{}>,
            value: string | null,
            reason: AutocompleteChangeReason
        ) => {
            const selectedVal =
                name === 'productType'
                    ? value
                          ?.split(premiumPlusProductNameIdentifier)
                          ?.pop()
                          ?.trim() ?? ''
                    : value
            handleChange(event, selectedVal, reason)
        },
        [handleChange, name]
    )
    return (
        <Autocomplete
            data-testid="autoComplete"
            options={getOptions}
            onInputChange={handleInputChange}
            getOptionLabel={(option) =>
                name === 'firmName' ? getFirmName(option) ?? '' : option
            }
            value={selectedValue}
            onChange={handleAutocompleteChange}
            onOpen={handleOpen}
            disabled={disabled}
            freeSolo
            loading={loading}
            ListboxProps={listBoxProps}
            renderInput={(params) => {
                const { InputProps, ...restParams } = params
                const { startAdornment, ...restInputProps } = InputProps
                return (
                    <>
                        <input
                            style={{ display: 'none' }}
                            type="text"
                            name={`fake-${name}`}
                        />
                        <TextField
                            {...restParams}
                            className={classes.autoCompleteInput}
                            inputProps={{
                                ...params.inputProps,
                                'aria-autocomplete': 'none',
                                autoComplete: 'off',
                            }}
                            InputProps={{
                                ...restInputProps,
                            }}
                            id={`${name}-input`}
                            data-testid={`${name}-input-test`}
                            name={name}
                            label={label ?? name}
                            variant="outlined"
                            error={!!hasError}
                        />
                    </>
                )
            }}
        />
    )
}
